import { httpClient } from './http-client';
import { isTwoFactorChallenge, type TwoFactorChallenge } from './twofactor-service';
import {
  LoginCredentials,
  RegisterCredentials,
  AuthResponse,
  RefreshTokenRequest,
  VerifyEmailRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ApiResponse,
  User,
} from '../types/api';

export class AuthService {
  private static readonly ENDPOINTS = {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    REFRESH: '/auth/refresh',
    VERIFY_EMAIL: '/auth/verify',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    LOGOUT: '/auth/logout',
    PROFILE: '/auth/profile',
  } as const;

  /**
   * Register a new user account
   */
  async register(credentials: RegisterCredentials): Promise<ApiResponse<AuthResponse>> {
    // Registering must never inherit whoever was signed in on this device.
    // A stale refresh token survived logout and re-authenticated the PREVIOUS
    // user moments after a new account was created, putting a brand-new signup
    // inside someone else's household.
    httpClient.clearStoredCredentials();
    const raw = await httpClient.post<AuthResponse | ApiResponse<AuthResponse>>(
      AuthService.ENDPOINTS.REGISTER,
      credentials
    );
    const data: AuthResponse = (raw as any)?.data ? (raw as any).data : (raw as AuthResponse);
    if (data?.access_token) {
      httpClient.setAuthToken(data.access_token);
      this.storeTokens(data);
    }
    return { data };
  }

  /**
   * Login with email and password.
   *
   * Returns EITHER a session or a two-factor challenge, never both. When the
   * account has a second factor the server withholds the tokens entirely, so
   * there is nothing to store here and the caller must complete the challenge.
   */
  async login(
    credentials: LoginCredentials
  ): Promise<ApiResponse<AuthResponse | TwoFactorChallenge>> {
    const raw = await httpClient.post<
      AuthResponse | TwoFactorChallenge | ApiResponse<AuthResponse | TwoFactorChallenge>
    >(AuthService.ENDPOINTS.LOGIN, credentials);
    const data = ((raw as any)?.data ? (raw as any).data : raw) as
      | AuthResponse
      | TwoFactorChallenge;

    if (!isTwoFactorChallenge(data)) {
      this.adoptSession(data);
    }
    return { data };
  }

  /**
   * Install a freshly issued session.
   *
   * Public because the two-factor exchange also ends in a real session, and
   * both entry points must persist it identically -- a second, slightly
   * different copy of this logic is how "signed in until you reload" bugs
   * happen.
   */
  adoptSession(auth: AuthResponse): void {
    if (!auth?.access_token) return;
    httpClient.setAuthToken(auth.access_token);
    this.storeTokens(auth);
  }

  /**
   * Refresh access token using refresh token
   */
  async refresh(refreshToken: string): Promise<ApiResponse<AuthResponse>> {
    const request: RefreshTokenRequest = { refresh_token: refreshToken };
    const raw = await httpClient.post<AuthResponse | ApiResponse<AuthResponse>>(
      AuthService.ENDPOINTS.REFRESH,
      request
    );
    const data: AuthResponse = (raw as any)?.data ? (raw as any).data : (raw as AuthResponse);
    if (data?.access_token) {
      httpClient.setAuthToken(data.access_token);
      this.storeTokens(data);
    }
    return { data };
  }

  /**
   * Verify email address with token
   */
  async verifyEmail(token: string): Promise<ApiResponse<{ message: string }>> {
    const request: VerifyEmailRequest = { token };
    return httpClient.post(AuthService.ENDPOINTS.VERIFY_EMAIL, request);
  }

  /**
   * Request password reset email
   */
  async forgotPassword(email: string): Promise<ApiResponse<{ message: string }>> {
    const request: ForgotPasswordRequest = { email };
    return httpClient.post(AuthService.ENDPOINTS.FORGOT_PASSWORD, request);
  }

  /**
   * Reset password with token
   */
  async resetPassword(
    token: string,
    password: string,
    confirmPassword: string
  ): Promise<ApiResponse<{ message: string }>> {
    const request: ResetPasswordRequest = {
      token,
      password,
      confirm_password: confirmPassword,
    };
    return httpClient.post(AuthService.ENDPOINTS.RESET_PASSWORD, request);
  }

  /**
   * Get current user profile
   */
  async getProfile(): Promise<ApiResponse<User>> {
    return httpClient.get(AuthService.ENDPOINTS.PROFILE);
  }

  /**
   * Logout and clear tokens
   */
  async logout(): Promise<void> {
    try {
      await httpClient.post(AuthService.ENDPOINTS.LOGOUT);
    } catch (error) {
      // Continue with local logout even if server logout fails
      console.warn('Server logout failed:', error);
    } finally {
      this.clearTokens();
    }
  }

  /**
   * Initialize auth state from stored tokens
   */
  initializeAuth(): boolean {
    const tokens = this.getStoredTokens();
    if (tokens?.access_token && !this.isTokenExpired(tokens.access_token)) {
      httpClient.setAuthToken(tokens.access_token);
      return true;
    }

    // Try to refresh if we have a refresh token
    if (tokens?.refresh_token) {
      this.attemptTokenRefresh(tokens.refresh_token);
      return true; // Optimistically return true, refresh will handle failures
    }

    return false;
  }

  /**
   * Check if user is currently authenticated
   */
  isAuthenticated(): boolean {
    const tokens = this.getStoredTokens();
    return !!(tokens?.access_token && !this.isTokenExpired(tokens.access_token));
  }

  /**
   * Get stored access token
   */
  getAccessToken(): string | null {
    const tokens = this.getStoredTokens();
    return tokens?.access_token || null;
  }

  /**
   * Store tokens in localStorage
   */
  private storeTokens(authResponse: AuthResponse): void {
    const tokens = {
      access_token: authResponse.access_token,
      refresh_token: authResponse.refresh_token,
      expires_at: Date.now() + authResponse.expires_in * 1000,
    };
    
    localStorage.setItem('folioworth_tokens', JSON.stringify(tokens));
    
    // Also store user data
    if (authResponse.user) {
      localStorage.setItem('folioworth_user', JSON.stringify(authResponse.user));
    }
    
    if (authResponse.household) {
      localStorage.setItem('folioworth_household', JSON.stringify(authResponse.household));
    }
  }

  /**
   * Get stored tokens from localStorage
   */
  private getStoredTokens(): {
    access_token: string;
    refresh_token: string;
    expires_at: number;
  } | null {
    try {
      const stored = localStorage.getItem('folioworth_tokens');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  /**
   * Clear all stored auth data
   */
  private clearTokens(): void {
    localStorage.removeItem('folioworth_tokens');
    localStorage.removeItem('folioworth_user');
    localStorage.removeItem('folioworth_household');
    httpClient.setAuthToken(null);
  }

  /**
   * Check if a JWT token is expired
   */
  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiry = payload.exp * 1000; // Convert to milliseconds
      return Date.now() >= expiry;
    } catch {
      return true; // Assume expired if we can't parse
    }
  }

  /**
   * Attempt to refresh token in background
   */
  private async attemptTokenRefresh(refreshToken: string): Promise<void> {
    try {
      await this.refresh(refreshToken);
    } catch (error) {
      console.warn('Token refresh failed:', error);
      this.clearTokens();
    }
  }
}

// Export singleton instance
export const authService = new AuthService();